import { z } from 'zod';

import type {
  MedicamentoVigenteResponse,
  SincronizarTratamentoDtoMedicamentosItem,
  SincronizarTratamentoDtoMedicamentosItemFrequencia,
  SincronizarTratamentoDtoMedicamentosItemVia,
  TratamentoEmCursoResponse,
} from '@/api/generated/model';
import { numeroParaCampo, paraNumero } from '@/features/historico/tratamento';

/**
 * Schema do prontuário COM o bloco de tratamento (Fase 1d).
 *
 * Fora do componente porque o editor de tratamento é um componente separado que
 * recebe o mesmo `control` — sem um tipo único, os dois lados divergiriam no
 * primeiro campo novo.
 *
 * ⚠️ **Todo campo numérico é `string` no formulário.** Não é desleixo: o
 * `<input>` devolve texto, o teclado brasileiro devolve vírgula, e coagir cedo
 * transforma "campo vazio" em `0` — que é um número válido para o zod e um erro
 * clínico para o resto. A conversão acontece uma vez só, ao montar o DTO.
 */

const DECIMAL = /^\d{1,9}([.,]\d{1,4})?$/;

function numeroPositivo(maximo: number, mensagem: string) {
  return z
    .string()
    .trim()
    .refine((v) => DECIMAL.test(v), mensagem)
    .refine((v) => paraNumero(v) > 0, mensagem)
    .refine((v) => paraNumero(v) <= maximo, `No máximo ${maximo}`);
}

function numeroPositivoOpcional(maximo: number, mensagem: string) {
  return z
    .string()
    .trim()
    .refine((v) => v === '' || DECIMAL.test(v), mensagem)
    .refine((v) => v === '' || paraNumero(v) > 0, mensagem)
    .refine((v) => v === '' || paraNumero(v) <= maximo, `No máximo ${maximo}`);
}

export const medicamentoSchema = z.object({
  /**
   * O id do `TratamentoMedicamento` que esta linha continua. Vazio = fármaco
   * novo neste atendimento.
   *
   * ⚠️ É ele que impede o mesmo fármaco de virar dois medicamentos correndo em
   * paralelo, com o histórico de dose partido em dois.
   */
  medicamentoId: z.string(),
  catalogoFarmacoId: z.string(),
  nomeLivre: z.string(),
  /** Só para exibir no gatilho do ComboBox depois que a busca troca a lista. */
  nome: z.string().trim().min(1, 'Escolha o fármaco'),
  doseValor: numeroPositivo(1_000_000, 'Informe a dose'),
  doseUnidade: z.string().trim().min(1, 'Informe a unidade').max(20),
  dosePorKg: numeroPositivoOpcional(100_000, 'Dose/kg inválida'),
  frequencia: z.string().min(1, 'Escolha a frequência'),
  frequenciaLivre: z.string().trim().max(120),
  via: z.string(),
  apresentacaoId: z.string(),
  nomeComercialLivre: z.string().trim().max(200),
  /** Rótulo da apresentação escolhida, para o gatilho do ComboBox. */
  apresentacaoRotulo: z.string(),
});

export type LinhaDeMedicamento = z.input<typeof medicamentoSchema>;

export const prontuarioSchema = z
  .object({
    tipo: z.string().min(1),
    titulo: z.string().trim().max(200),
    conteudo: z.string().trim().min(1, 'Escreva o conteúdo do prontuário.').max(20_000),
    atendidoEm: z.string().min(1, 'Informe quando o atendimento aconteceu.'),
    /**
     * `''` = **não declarado**, e é um estado válido — é o que todo prontuário
     * antigo e todo prontuário vindo do app têm. `SEM_TRATAMENTO` é uma
     * afirmação clínica; a ausência dela não é.
     */
    declaracaoTratamento: z.string(),
    tituloTratamento: z.string().trim().max(200),
    evolucao: z.string(),
    evolucaoObservacoes: z.string().trim().max(2000),
    medicamentos: z.array(medicamentoSchema),
  })
  .superRefine((valores, ctx) => {
    if (valores.declaracaoTratamento !== 'EM_TRATAMENTO') return;

    if (!valores.tituloTratamento.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['tituloTratamento'],
        message: 'Dê um nome ao tratamento.',
      });
    }

    if (valores.medicamentos.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['medicamentos'],
        message: 'Acrescente ao menos um medicamento, ou marque "Sem tratamento".',
      });
    }

    valores.medicamentos.forEach((linha, indice) => {
      if (linha.frequencia === 'OUTRA' && !linha.frequenciaLivre.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['medicamentos', indice, 'frequenciaLivre'],
          message: 'Descreva a frequência.',
        });
      }
    });
  });

export type FormularioProntuario = z.input<typeof prontuarioSchema>;

/**
 * O ANEXO (item 16) usa o MESMO formulário do prontuário — é o que deixa o
 * `EditorDeTratamento` servir aos dois sem uma cópia. Os campos mudam de papel:
 *
 *  · `titulo`   → título do anexo (aqui obrigatório);
 *  · `conteudo` → texto do anexo (aqui opcional);
 *  · `declaracaoTratamento === 'EM_TRATAMENTO'` → "registrar tratamento neste
 *    anexo" ligado. Com tratamento vigente, a lista pode ficar vazia (retira
 *    todos os fármacos); sem, é início de tratamento e pede título + fármaco.
 *
 * Os demais (`tipo`, `atendidoEm`, `evolucao`...) ficam no formulário sem uso.
 */
export function anexoSchema(temTratamentoVigente: boolean) {
  return z
    .object({
      titulo: z.string().trim().min(1, 'Dê um título ao anexo.').max(120, 'No máximo 120 caracteres.'),
      conteudo: z.string().trim().max(20_000),
      declaracaoTratamento: z.string(),
      tituloTratamento: z.string().trim().max(200),
      medicamentos: z.array(medicamentoSchema),
      // Sem uso no anexo — existem para o tipo ser o `FormularioProntuario`.
      tipo: z.string(),
      atendidoEm: z.string(),
      evolucao: z.string(),
      evolucaoObservacoes: z.string(),
    })
    .superRefine((valores, ctx) => {
      if (valores.declaracaoTratamento !== 'EM_TRATAMENTO') return;

      if (!temTratamentoVigente && !valores.tituloTratamento.trim()) {
        ctx.addIssue({ code: 'custom', path: ['tituloTratamento'], message: 'Dê um nome ao tratamento.' });
      }
      if (!temTratamentoVigente && valores.medicamentos.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['medicamentos'], message: 'Acrescente ao menos um medicamento.' });
      }
      valores.medicamentos.forEach((linha, indice) => {
        if (linha.frequencia === 'OUTRA' && !linha.frequenciaLivre.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: ['medicamentos', indice, 'frequenciaLivre'],
            message: 'Descreva a frequência.',
          });
        }
      });
    });
}

export function linhaVazia(): LinhaDeMedicamento {
  return {
    medicamentoId: '',
    catalogoFarmacoId: '',
    nomeLivre: '',
    nome: '',
    doseValor: '',
    doseUnidade: '',
    dosePorKg: '',
    frequencia: 'SID',
    frequenciaLivre: '',
    via: '',
    apresentacaoId: '',
    nomeComercialLivre: '',
    apresentacaoRotulo: '',
  };
}

function linhaDoMedicamento(medicamento: MedicamentoVigenteResponse): LinhaDeMedicamento {
  const dose = medicamento.doseVigente;

  return {
    medicamentoId: medicamento.medicamentoId,
    catalogoFarmacoId: medicamento.catalogoFarmacoId ?? '',
    nomeLivre: medicamento.nomeLivre ?? '',
    nome: medicamento.nome,
    doseValor: numeroParaCampo(dose?.doseValor),
    doseUnidade: dose?.doseUnidade ?? '',
    dosePorKg: numeroParaCampo(dose?.dosePorKg),
    frequencia: dose?.frequencia ?? 'SID',
    frequenciaLivre: dose?.frequenciaLivre ?? '',
    via: dose?.via ?? '',
    apresentacaoId: dose?.apresentacaoId ?? '',
    nomeComercialLivre: dose?.nomeComercialLivre ?? '',
    apresentacaoRotulo: dose?.apresentacaoNome ?? dose?.nomeComercialLivre ?? '',
  };
}

/**
 * O tratamento em curso vira as linhas do editor — é a CONTINUIDADE entre
 * atendimentos.
 *
 * O editor abre já preenchido com o que valia na data da consulta, nunca em
 * branco: quem não mexer em nada salva sem gerar linha nenhuma
 * (`semAlteracao`), que é o desfecho comum de um retorno.
 */
export function linhasDoTratamento(tratamento: TratamentoEmCursoResponse): LinhaDeMedicamento[] {
  return tratamento.medicamentos.map(linhaDoMedicamento);
}

/**
 * As linhas do editor → o corpo de `POST /tratamentos/:id/sincronizar`.
 *
 * Manda-se o ESTADO como ficou na tela; quem calcula a diferença é o servidor,
 * o único que sabe o que valia em `atendidoEm`.
 *
 * ⚠️ `pesoKgReferencia` acompanha a dose/kg e **não entra no diff** do servidor
 * — é o que explica o número depois, quando o animal já pesa outra coisa.
 */
export function itensParaSincronizar(
  linhas: LinhaDeMedicamento[],
  pesoKg: number | null,
): SincronizarTratamentoDtoMedicamentosItem[] {
  return linhas.map((linha) => {
    const dosePorKg = linha.dosePorKg.trim() ? paraNumero(linha.dosePorKg) : null;

    return {
      medicamentoId: linha.medicamentoId || null,
      catalogoFarmacoId: linha.catalogoFarmacoId || null,
      nomeLivre: linha.catalogoFarmacoId ? null : linha.nomeLivre.trim() || linha.nome.trim(),
      doseValor: paraNumero(linha.doseValor),
      doseUnidade: linha.doseUnidade.trim(),
      dosePorKg,
      pesoKgReferencia: dosePorKg === null ? null : pesoKg,
      frequencia: linha.frequencia as SincronizarTratamentoDtoMedicamentosItemFrequencia,
      frequenciaLivre: linha.frequencia === 'OUTRA' ? linha.frequenciaLivre.trim() || null : null,
      via: (linha.via || null) as SincronizarTratamentoDtoMedicamentosItemVia,
      apresentacaoId: linha.apresentacaoId || null,
      nomeComercialLivre: linha.apresentacaoId ? null : linha.nomeComercialLivre.trim() || null,
    };
  });
}

/**
 * Frase do resultado da sincronização.
 *
 * ⚠️ `null` quando `semAlteracao` — e aí a tela não diz nada. Anunciar "0
 * ajustes" transformaria o desfecho normal de um retorno em quase-erro.
 */
export function descreverSincronizacao(resultado: {
  vigenciasCriadas: number;
  medicamentosCriados: number;
  medicamentosEncerrados: number;
  semAlteracao: boolean;
}): string | null {
  if (resultado.semAlteracao) return null;

  const partes: string[] = [];

  if (resultado.vigenciasCriadas > 0) {
    partes.push(
      resultado.vigenciasCriadas === 1
        ? '1 ajuste registrado'
        : `${resultado.vigenciasCriadas} ajustes registrados`,
    );
  }
  if (resultado.medicamentosCriados > 0) {
    partes.push(
      resultado.medicamentosCriados === 1
        ? '1 fármaco acrescentado'
        : `${resultado.medicamentosCriados} fármacos acrescentados`,
    );
  }
  if (resultado.medicamentosEncerrados > 0) {
    partes.push(
      resultado.medicamentosEncerrados === 1
        ? '1 fármaco retirado'
        : `${resultado.medicamentosEncerrados} fármacos retirados`,
    );
  }

  return partes.length > 0 ? `${partes.join(' · ')}.` : null;
}
